import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Action } from 'redux';

import { CallStatus } from '../../../../lib/enums';
import lang from '../../../../lib/language';
import { RootState } from '../../../../lib/store';
import { setBankAccountDetailsOnCreate } from '../../../../lib/store/crm';
import {
  registerLinkedAccount,
  setPaymentCallStatusToInitial,
} from '../../../../lib/store/payment';
import { PaymentCall, RegisterLinkedAccountPayload } from '../../../../lib/store/payment/types';
import { TextHelpers } from '../../../../lib/util/TextHelpers';

import imageUploadBankStatement from '../../../../assets/bigImages/image_bank-statement.png';
import iconFilledCheck from '../../../../assets/img/icon-filled-check.svg';
import { useBrowserWidth, useIsMobile } from '../../../../hooks';
import Button from '../../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../../../util/routes';
import { BaseModal, ClickableDiv, Document, LogoAndCloseButton, Preloader } from '../../..';
import ContainerBackButton from '../../../ContainerBackButton/ContainerBackButton';
import GoBackButton from '../../../GoBackButton/GoBackButton';
import AddBankAccountModal from '../../../PayoutsModals/AddBankAccountModal';
import UploadBankStatementImage from '../../../UploadBankStatementImage/UploadBankStatementImage';
import AddBankAccountScreen from '../AddBankAccountScreen/AddBankAccountScreen';

import styles from './UploadBankStatement.module.scss';

type Props = {} & ReduxProps;

const UploadBankStatement = (props: Props) => {
  const {
    bankAccountDetailsOnCreate,
    requestRegisterLinkedAccount,
    regLinkedAccount,
    setPaymentStatusByCall,
    setBankAccountValuesOnCreate,
  } = props;

  const { isMobile } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();
  const navigate = useNavigate();
  const location = useLocation();
  const addYourBankAccountPath = UI_ROUTES.payouts + UI_ROUTES.addYourBankAccount;
  const isDefaultGoBackButton = browserWidthSize >= 989 && !isMobile;

  const [uploadBackStatement, setUploadBackStatement] = useState<File>();
  const [hasRequest, setHasRequest] = useState<boolean>(false);
  const [isInitialised, setIsInitialised] = useState<boolean>(false);

  const isBtnDisabled: boolean = (!uploadBackStatement && !!requestRegisterLinkedAccount?.status)
    || requestRegisterLinkedAccount?.status === CallStatus.PENDING;

  useEffect(() => {
    const isEmptyData = Object.values(bankAccountDetailsOnCreate.account_info).some(
      (value) => value.toString().trim() === '' || value === null || value === undefined,
    );
    if (isEmptyData) {
      navigate(addYourBankAccountPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isInitialised) {
      setPaymentStatusByCall();
      setIsInitialised(true);
    }
  }, [isInitialised, setPaymentStatusByCall]);

  const handleConfirm = () => {
    if (uploadBackStatement) {
      const dataToSaveRegLinkedAccount = {
        file: uploadBackStatement,
        iban: bankAccountDetailsOnCreate.account_info.iban,
        bankName: bankAccountDetailsOnCreate.account_info.bank_name,
        swiftCode: bankAccountDetailsOnCreate.account_info.bic,
        bankCountryId: bankAccountDetailsOnCreate.account_info.bank_country_id,
        currencyCode: bankAccountDetailsOnCreate.account_info.currency_code,
      };
      regLinkedAccount(dataToSaveRegLinkedAccount);
      setHasRequest(true);
    }
  };

  useEffect(() => {
    if (hasRequest) {
      if (
        requestRegisterLinkedAccount
        && requestRegisterLinkedAccount.status === CallStatus.ERROR
      ) {
        const dataToSaveRegLinkedAccount = {
          file: uploadBackStatement,
          iban: bankAccountDetailsOnCreate.account_info.iban,
          bankName: bankAccountDetailsOnCreate.account_info.bank_name,
          swiftCode: bankAccountDetailsOnCreate.account_info.bic,
          bankCountryId: bankAccountDetailsOnCreate.account_info.bank_country_id,
          currencyCode: bankAccountDetailsOnCreate.account_info.currency_code,
        };
        navigate(
          {
            pathname: UI_ROUTES.error,
          },
          {
            state: {
              errorCodeAddBankAccount: requestRegisterLinkedAccount.errorCode,
              target: location.pathname,
              dataToSaveRegLinkedAccount,
            },
          },
        );
      } else if (
        requestRegisterLinkedAccount
        && requestRegisterLinkedAccount.status === CallStatus.READY
      ) {
        setHasRequest(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRequest, requestRegisterLinkedAccount]);

  const handleBack = () => {
    navigate(addYourBankAccountPath);
  };

  const handleClose = () => {
    setBankAccountValuesOnCreate({});
    return navigate(UI_ROUTES.payouts);
  };

  const handleContinue = () => {
    setBankAccountValuesOnCreate({});
    navigate(UI_ROUTES.payouts + UI_ROUTES.withdrawalAmount);
  };

  return (
    <>
      {hasRequest && (
        <Preloader owner="Upload bank statement" isFullScreen stylePreset="customOne" />
      )}
      <div className={styles.wrapperContainer}>
        <LogoAndCloseButton
          displayCloseButton={!isMobile}
          displayBackButton={isDefaultGoBackButton}
          onBackBtnPress={handleBack}
          onClose={handleClose}
          newWebGoBackButtonDesign
        />
        {!isDefaultGoBackButton && !isMobile && (
          <div className={styles.goBackButtonContainer}>
            <ContainerBackButton onBackBtnPress={handleBack} />
          </div>
        )}
        {isMobile && (
          <div className={styles.mobileBackContainer}>
            <GoBackButton onBackBtnPress={handleBack} />
          </div>
        )}
        <div className={styles.containerContent}>
          <div className={styles.wrapperContent}>
            <div className={styles.title} id="uploadBankStatementTitle">
              {lang.mobileAddBankAccUploadBankStatementBtn()}
            </div>
            {isMobile && (
              <div className={styles.imgWrapper}>
                <img className={styles.image} src={imageUploadBankStatement} alt="Bank Statement" />
              </div>
            )}
            {!isMobile && (
              <div className={styles.descriptionWrapper}>
                <span className={styles.description}>{lang.commonMakeSureFollowing()}</span>
              </div>
            )}
            <div className={styles.wrapperDocumentInfo}>
              <img className={styles.iconCheck} src={iconFilledCheck} alt="icon filled check" />
              <p className={styles.text}>{lang.mobileBankStatementIssuedLastThreeMonths()}</p>
            </div>
            <div className={styles.wrapperDocumentInfo}>
              <img className={styles.iconCheck} src={iconFilledCheck} alt="icon filled check" />
              <p className={styles.text}>{lang.mobileNameAndAddressVisible()}</p>
            </div>
            <div className={styles.wrapperDocumentInfo}>
              <img className={styles.iconCheck} src={iconFilledCheck} alt="icon filled check" />
              <p className={styles.text}>{lang.commonUploadDocumentWebLabel3()}</p>
            </div>
            {isMobile && (
              <div className={styles.wrapperDocumentInfo}>
                <img className={styles.iconCheck} src={iconFilledCheck} alt="icon filled check" />
                <p className={styles.text}>
                  {lang.commonUploadDocumentTypesHint(TextHelpers.getAllowedFileExtensionNames())}
                </p>
              </div>
            )}
            {isMobile ? (
              <UploadBankStatementImage
                uploadDisabled={
                  !uploadBackStatement
                  && requestRegisterLinkedAccount
                  && requestRegisterLinkedAccount.status === CallStatus.PENDING
                }
                handleUploadFileAsFileFormat={setUploadBackStatement}
                handleConfirm={handleConfirm}
              />
            ) : (
              <>
                <div className={styles.wrapperDropYourImage}>
                  <ClickableDiv onClick={() => setUploadBackStatement(undefined)}>
                    <Document
                      idInput="uploadStatement"
                      idRemove="removeStatement"
                      ariaLabelInput="upload statement"
                      ariaLabelRemove="remove statement"
                      handleUploadFileAsFileFormat={setUploadBackStatement}
                      extraStyles
                      height={'auto'}
                    />
                  </ClickableDiv>
                </div>
                <Button
                  id="uploadBankStatementBackConfirm"
                  fullWidth
                  className={styles.continueBtn}
                  disabled={isBtnDisabled}
                  onClick={handleConfirm}
                  variant={ButtonTypesEnum.Primary}
                >
                  {lang.commonContinueButton()}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <BaseModal
        isOpen={
          !isMobile
          && !!requestRegisterLinkedAccount
          && requestRegisterLinkedAccount.status === CallStatus.READY
        }
      >
        <AddBankAccountModal
          handleContinue={handleContinue}
          textInfoModal={{
            title: lang.mobileBankDetailsSetTitle(),
            description1: lang.webPopupBankDetailsDescription1(),
            description2: lang.webPopupBankDetailsDescription2(),
          }}
        />
      </BaseModal>
      <AddBankAccountScreen
        isOpen={
          isMobile
          && !!requestRegisterLinkedAccount
          && requestRegisterLinkedAccount.status === CallStatus.READY
        }
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  bankAccountDetailsOnCreate: state.crm.enroll.bankAccountDetailsOnCreate,
  requestRegisterLinkedAccount: state.payment.statusByCall[PaymentCall.registerLinkedAccount],
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  regLinkedAccount: (data: RegisterLinkedAccountPayload) => dispatch(registerLinkedAccount(data)),
  setPaymentStatusByCall: () => dispatch(setPaymentCallStatusToInitial(null)),
  setBankAccountValuesOnCreate: (data: any) => dispatch(setBankAccountDetailsOnCreate(data)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(UploadBankStatement);
